import { useEffect, useState } from "react";
import type { Heading } from "@/lib/blog/use-headings";

interface Props {
  headings: Heading[];
}

const HEADER_OFFSET = 96; // sticky-Header + Atemraum

function scrollToHeading(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
  history.replaceState(null, "", `#${id}`);
}

function useActiveHeading(headings: Heading[]) {
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null);
  useEffect(() => {
    if (headings.length === 0) return;
    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.intersectionRatio);
          } else {
            visible.delete(entry.target.id);
          }
        }
        const top = [...visible.entries()].sort((a, b) => b[1] - a[1])[0];
        if (top) setActive(top[0]);
      },
      { rootMargin: `-${HEADER_OFFSET}px 0px -55% 0px`, threshold: [0, 0.5, 1] },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);
  return active;
}

function TocList({ headings, active }: { headings: Heading[]; active: string | null }) {
  return (
    <ul className="space-y-1.5">
      {headings.map((h) => {
        const isActive = h.id === active;
        return (
          <li key={h.id} className={h.level === 3 ? "ml-3" : ""}>
            <a
              href={`#${h.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToHeading(h.id);
              }}
              aria-current={isActive ? "location" : undefined}
              className={
                "block border-l-2 py-1 pl-3 font-body text-sm leading-snug transition-colors " +
                (isActive
                  ? "border-brand-button font-display font-extrabold text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground")
              }
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

export function TableOfContentsDesktop({ headings }: Props) {
  const active = useActiveHeading(headings);
  if (headings.length < 2) return null;
  return (
    <nav aria-label="Inhalt" className="hidden min-h-0 flex-1 flex-col lg:flex">
      <p className="mb-3 font-display text-xs font-extrabold uppercase tracking-wide text-primary">
        In diesem Artikel
      </p>
      <div className="min-h-0 overflow-y-auto pr-2">
        <TocList headings={headings} active={active} />
      </div>
    </nav>
  );
}

export function TableOfContentsMobile({ headings }: Props) {
  const active = useActiveHeading(headings);
  if (headings.length < 2) return null;
  return (
    <details className="group mb-10 rounded-md border border-border bg-secondary/40 lg:hidden">
      <summary className="cursor-pointer list-none px-4 py-3 font-display text-sm font-extrabold text-primary marker:hidden">
        Inhalt anzeigen
        <span className="float-right transition-transform group-open:rotate-180">▾</span>
      </summary>
      <div className="border-t border-border px-4 py-3">
        <TocList headings={headings} active={active} />
      </div>
    </details>
  );
}
